<script setup>
import Drawer from 'primevue/drawer'
import Avatar from 'primevue/avatar'
import {useAuthStore} from '~/store/auth'
import Menu from 'primevue/menu'
import DynamicMenu from '~/components/common/DynamicMenu.vue'
import MobileDynamicMenu from '~/components/common/MobileDynamicMenu.vue'
import {useCartStore} from '~/store/cart'
import {useConfigStore} from '~/store/config'
import {concat} from 'lodash'
import {getStaticRightMenuItems, getUserMenuItems} from '~/utils/menuUtils'
import SearchBar from '~/components/common/SearchBar.vue'

const router = useRouter()
const config = useRuntimeConfig()
const showMenu = ref(false)
const authStore = useAuthStore()
const cartStore = useCartStore()
const isMobile = useIsMobile()
const configStore = useConfigStore()
const currentRoute = useRoute()
const showSearchBar = ref(false)

const userMenuRef = ref(null)
const isAuth = computed(() => {
  return authStore.isAuth
})
const isAuthWithAvatar = computed(() => {
  return authStore.isAuth && authStore.user?.avatar || false
})
const isAuthWithInitials = computed(() => {
  return !isAuthWithAvatar.value && authStore.isAuth && (!!authStore.user?.initials || false)
})
const currentAvatar = computed(() => {
  return authStore.user?.avatar || null
})
const currentInitials = computed(() => {
  return authStore.user?.initials || null
})
const cartItemsCount = computed(() => {
  return cartStore.itemsCount
})
const userMenuItems = computed(() => {
  return getUserMenuItems(isAuth.value)
})

const onSearchClicked = () => {
  showSearchBar.value = !showSearchBar.value
}
const staticRightMenuItems = computed(() => getStaticRightMenuItems(cartItemsCount.value, onSearchClicked))
const leftMenuItems = computed(() => configStore.getMenuItems('main_menu_left'))
const rightMenuItems = computed(() => {
  return concat(configStore.getMenuItems('main_menu_right'), staticRightMenuItems.value)
})
const mobileMenuItems = computed(() => {
  return concat(leftMenuItems.value, rightMenuItems.value).filter(item => !item?.excludeMobile)
})

function toggleMobileMenu() {
  showMenu.value = !showMenu.value
}

async function openUserMenu(event) {
  userMenuRef.value.toggle(event)
}

async function doLogout() {
  try {
    await authStore.logout()
  } catch (e) {
    console.error(e)
  }
}

async function onDoSearch(query) {
  try {
    await router.push({name: 'busqueda', query: {q: query}})
    showSearchBar.value = false
  } catch (e) {
    console.error(e)
  }
}

watch(() => isMobile.value, (value) => {
  if (!value && showMenu.value) {
    showMenu.value = false
  }
})
watch(currentRoute, () => {
  if (showMenu.value) {
    showMenu.value = false
  }
})
</script>

<template>
  <nav class="sticky w-full bg-white z-20 top-0 left-0">
    <!-- avisos -->
    <div class="text-center text-xs italic py-2 px-3 bg-[#dcd6ce] text-[#363330]">Envío gratis a partir de 300€ en España (sólo Península)</div>
    <!-- menú dinámico-->
    <div class="grid grid-cols-12 gap-0 lg:gap-4 py-4 lg:pt-5 px-3 shadow-[0_35px_60px_-15px_rgba(0,0,0,0.15)]">
      <!-- menú dinámico izquierda -->
      <DynamicMenu
        class="hidden gap-5 order-1 lg:flex lg:flex-row col-span-12 lg:col-span-5 italic justify-start items-center px-3 lg:pr-0 lg:pl-5 text-center lg:text-left uppercase lg:text-[0.9rem]"
        :items="leftMenuItems"
      />
      <!-- drawer -->
      <Drawer class="sidebar-website-menu" v-model:visible="showMenu">
        <template #header>
          <div class="italic mr-2 bg-[#dcd6ce] text-[#363330] p-2 text-[14px]" @click.prevent="toggleMobileMenu">Cerrar Menú
          </div>
        </template>
        <div class="flex flex-col mb-4 w-full">
          <IconField class="w-full">
            <InputIcon class="pi pi-search !text-red-800"/>
            <InputText class="text-sm italic !w-full" placeholder="Buscar"
                       @keydown.enter="(event) => onDoSearch(event.target.value)"/>
          </IconField>
        </div>
        <MobileDynamicMenu :items="mobileMenuItems" @logout="doLogout"/>
      </Drawer>
      <!-- logo desktop-->
      <div class="order-2 col-span-12 lg:col-span-2 justify-center mb-4 lg:mb-0 hidden lg:visible lg:flex">
        <NuxtLink to="/">
          <img class="h-[30px] lg:h-[25px]" src="/images/logo-desktop.png" :alt="config.public.pageTitle"/>
        </NuxtLink>
      </div>
      <!-- logo mobile-->
      <div class="order-3 col-span-12 lg:col-span-5 text-md italic flex justify-between lg:justify-end px-3 lg:pl-0 lg:pr-8 gap-3 text-center lg:text-left">
        <div class="visible flex items-center lg:hidden">
          <NuxtLink to="/">
            <img class="h-[45px] lg:h-[25px]" src="/images/logo-mobile.png" :alt="config.public.pageTitle"/>
          </NuxtLink>
        </div>
        <div class="flex justify-end items-center gap-2 lg:gap-5">
          <!-- menú dinámico derecha -->
          <DynamicMenu
            class="hidden gap-5 lg:flex lg:flex-row col-span-12 lg:col-span-4 italic justify-end items-center px-3 lg:pr-0 lg:pl-0 text-center lg:text-left uppercase lg:text-[0.9rem]"
            :items="rightMenuItems"
          />
          <NuxtLink
            class="flex relative text-red-800 bg-white p-2 lg:p-0 rounded items-center lg:hidden"
            to="/compra"
          >
            <i class="pi pi-shopping-bag text-[23px] mr-0 lg:mr-1 !block lg:inline-block"/>
            <Transition>
              <!-- NÚMERO DE ARTÍCULOS EN COMPRA-->
              <Badge
                v-show="cartItemsCount > 0"
                class="absolute right-[-4px] top-[-4px] lg:right-[-9px] lg:top-[-9px]"
                :value="cartItemsCount"
                severity="danger"
                size="small"
              />
            </Transition>
          </NuxtLink>
          <NuxtLink href="#" @click.prevent="openUserMenu" class="flex items-center">
            <Avatar v-if="isAuthWithAvatar" class="avatar-item w-[32px] h-[32px] rounded" :image="currentAvatar"/>
            <Avatar v-if="isAuthWithInitials" class="avatar-item w-[32px] h-[32px] rounded" :label="currentInitials"/>
            <Avatar v-if="!isAuth" icon="pi pi-user" class="flex justify-center items-center avatar-item"/>
          </NuxtLink>
          <a
            class="flex text-[#363330] bg-[#dcd6ce] ml-2 p-[7px] lg:p-0 rounded items-center lg:hidden"
            href="#" @click.prevent="toggleMobileMenu">
            <i class="pi pi-bars mr-0 lg:mr-1 !block lg:inline-block"/>
          </a>
        </div>
      </div>
    </div>
    <!--  -->
    <Menu ref="userMenuRef" id="overlay_user" :model="userMenuItems" :popup="true">
      <template #item="{ item, props }">
        <nuxt-link class="text-red-800" v-if="item.route" v-slot="{ href, navigate }" :to="item.route" custom>
          <a v-ripple :href="href" v-bind="props.action" @click="navigate">
            <span :class="item.icon"/>
            <span class="ml-2">{{ item.label }}</span>
          </a>
        </nuxt-link>
        <a v-else v-ripple :href="item.url" :target="item.target" v-bind="props.action">
          <span :class="item.icon"/>
          <span class="ml-2">{{ item.label }}</span>
        </a>
      </template>
    </Menu>
    <!-- barra de navegación -->
    <SearchBar :show="showSearchBar" @search="onDoSearch"/>
  </nav>
</template>
